<template>
  <StudioDialog
    :title="maxSelections === 1 ? 'Select Video' : 'Select Videos'"
    size="2xlarge"
    :primaryActionTitle="selectable ? 'Save' : ''"
    @primaryAction="handleSave"
    @close="$emit('close')"
  >
    <div class="tw-h-[80vh]">
      <VideoLibraryTable
        ref="videoLibraryTable"
        :context="context"
        :selectable="selectable"
        :currentPage="currentPage"
        :sortOrder="sortOrder"
        :search="search"
        :maxSelections="maxSelections"
        :initialSelectedVideoAssets="initialSelectedVideoAssets"
        :series="[]"
        :events="[]"
        :tags="[]"
        :pageSize="25"
        :sources="[]"
        :createdBy="[]"
        :workspaceFilter="workspaceFilter"
        @update:currentPage="currentPage = $event"
        @update:sortOrder="sortOrder = $event"
        @update:search="search = $event"
      />
    </div>
    <template #footer-content>
      <div class="tw-flex tw-w-full tw-justify-start tw-gap-2 tw-pl-2">
        <div v-if="selectedText" class="tw-flex tw-h-8 tw-items-center tw-rounded-md tw-py-1 tw-text-xs tw-font-medium">
          <p>{{ selectedText }}</p>
        </div>
      </div>
    </template>
  </StudioDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { VideoUploadContext } from './utils';
import { type VideoAsset, type VideoLibrarySortOrder } from './videoLibraryService';
import VideoLibraryTable, { VideoLibraryWorkspaceFilter } from './VideoLibraryTable/VideoLibraryTable.vue';

import StudioDialog from '@/studioComponents/StudioDialog.vue';

const props = withDefaults(
  defineProps<{
    initialSelectedVideoAssets?: VideoAsset[];
    selectable?: boolean;
    maxSelections?: number;
    context?: VideoUploadContext;
  }>(),
  {
    initialSelectedVideoAssets: () => [],
    selectable: true,
    maxSelections: Infinity,
    context: ''
  }
);

const route = useRoute();
const store = useStore();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'select-videos', videos: VideoAsset[]): void;
}>();

const currentPage = ref(1);
const sortOrder = ref<VideoLibrarySortOrder>('-created_at');
const search = ref('');

const videoLibraryTable = ref<InstanceType<typeof VideoLibraryTable>>();

const currentSelectedVideoAssets = computed(() => videoLibraryTable.value?.selectedVideoAssets || []);

const selectedText = computed(() => {
  if (!props.selectable) {
    return '';
  }

  if (props.maxSelections === 1) {
    return '';
  }

  return `Selected: ${currentSelectedVideoAssets.value.length} ${
    props.maxSelections < Infinity ? `of ${props.maxSelections}` : ''
  }`;
});

const workspaceFilter = computed<VideoLibraryWorkspaceFilter>(() => {
  let filter: VideoLibraryWorkspaceFilter = { id: undefined, hidden: false };

  let workspaceId;
  if (route.params.seriesId) {
    const series = store.getters['studioSeriesList/getCurrentSeriesById'](route.params.seriesId);
    if (series?.type === 'repeating') {
      workspaceId = store.getters['studioEventList/getCurrentEvent']?.workspace?.id;
    }
  } else if (route.params.eventId) {
    workspaceId = store.getters['studioEventList/getCurrentEvent']?.workspace?.id;
  } else if (route.params.templateId) {
    workspaceId = store.getters['studioTemplateList/getCurrentTemplate']?.workspace?.id;
  }

  if (workspaceId) {
    filter = { id: workspaceId, hidden: !!workspaceId };
  }

  return filter;
});

const handleSave = () => {
  emit('select-videos', videoLibraryTable.value?.selectedVideoAssets || []);
};
</script>
